'use client'

import { useParams, usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useMemo, useRef } from 'react'

import { useCatalogBrandsInitialData } from '@/features/catalog-brands/initial-data'
import { useCatalogUrl } from '@/features/catalog-shared/url-context'

import { FilterProperties } from '@/entities/catalog-brands'
import { useGetCatalogBrandsFilterQuery } from '@/entities/catalog-brands'
import { getRegionCookies } from '@/entities/regions'

import { buildFilterSefUrl } from '@/shared/lib/utils/filter'

import { getFilterJsonFromProperties } from '../lib/get-filter-json-properties'

const getAppliedFiltersCount = (appliedFilters: FilterProperties[]) =>
	appliedFilters
		.map(({ VALUES }) => VALUES)
		.flat()
		.filter(({ CHECKED }) => CHECKED).length

export const useBrandFilter = () => {
	const router = useRouter()
	const pathname = usePathname()
	const searchParams = useSearchParams()
	const params = useParams()
	const urlCtx = useCatalogUrl()
	const search = urlCtx?.search ?? searchParams.toString()
	const resolvedUrl = urlCtx?.resolvedUrl ?? `${pathname}${search ? `?${search}` : ''}`
	const resolvedPathname = urlCtx?.pathname ?? pathname
	const regionCookies = getRegionCookies()
	const { initialFilterData, initialResolvedUrl } = useCatalogBrandsInitialData()

	// Shallow-навигация (если раздел обёрнут в CatalogUrlProvider) — без ремаунта; иначе fallback.
	const navigate = (url: string) => {
		if (urlCtx?.updateResolvedUrl) {
			urlCtx.updateResolvedUrl(url)
		} else {
			void router.push(url)
		}
	}
	const initialResolvedUrlRef = useRef<string>(initialResolvedUrl)
	const canUseInitialForCurrentRoute = initialResolvedUrlRef.current === resolvedUrl

	const brandCode = (params.brand as string) || ''
	const categoryParams = (params.category as string[]) || []
	const categoryCode = Array.isArray(categoryParams) && categoryParams.length > 0 ? categoryParams[0] : ''

	const {
		data,
		currentData,
		isFetching: isFilterFetching
	} = useGetCatalogBrandsFilterQuery(
		{ brandCode, categoryCode, resolvedUrl, regionCookies },
		{ skip: !brandCode || !categoryCode }
	)

	const safeFilterData = canUseInitialForCurrentRoute ? data ?? currentData ?? initialFilterData : data ?? currentData

	const filterUrl = JSON.stringify(getFilterJsonFromProperties(safeFilterData, categoryCode))

	const appliedFiltersCount = useMemo(
		() => getAppliedFiltersCount(safeFilterData?.PROPERTIES || []),
		[safeFilterData?.PROPERTIES]
	)

	const onFilterChange = async (newFilterUrl: string) => {
		if (!brandCode || !categoryCode) return

		const baseUrl = `/brand/${brandCode}/${categoryCode}/`
		const isEmptyFilter = !newFilterUrl || newFilterUrl === '{}'

		if (isEmptyFilter && resolvedPathname.includes('/filter/')) {
			navigate(baseUrl)
			return
		}

		if (!safeFilterData?.PROPERTIES) {
			navigate(baseUrl)
			return
		}

		const sefUrl = buildFilterSefUrl({
			baseUrl,
			filterJson: newFilterUrl,
			properties: safeFilterData.PROPERTIES,
			sortParam: new URLSearchParams(search).get('sort') ?? undefined
		})

		navigate(sefUrl)
	}

	const clearFilter = async () => {
		if (brandCode && categoryCode) {
			navigate(`/brand/${brandCode}/${categoryCode}/`)
		} else if (brandCode) {
			navigate(`/brand/${brandCode}/`)
		}
	}

	return {
		filterData: safeFilterData,
		properties: safeFilterData?.PROPERTIES,
		title: safeFilterData?.SEO?.SECTION_PAGE_TITLE,
		seo: safeFilterData?.SEO,
		filterFaq: safeFilterData?.FAQ,
		onFilterChange,
		clearFilter,
		appliedFiltersCount,
		isLoading: isFilterFetching,
		filterUrl,
		categoryCode,
		brandCode
	}
}
